/**
 * The graph window's numbers: what the nodal view draws and within which budgets (names,
 * counts, arrowheads), its look (alphas, dashes, sizes in CSS pixels), how the simulation cools
 * and when painting is deferred, and how far a press moves before it is a drag. The class
 * diagram, the board and the document view, inside this window, take theirs from here too when
 * they mean the same; their own are beside them (`board/constants.ts`, `document/constants.ts`).
 */

/** At most this many node names in one picture (the most important ones). */
export const LABEL_BUDGET = 400;

/** How far beyond the canvas's edges things are still drawn, in pixels. */
export const SCREEN_MARGIN_PX = 40;

/** Below this length on screen (pixels), a link is drawn without its arrow head. */
export const HEAD_MIN_PX = 14;

/** Below this radius on screen (pixels), a node is drawn without its comment dot. */
export const DOT_MIN_PX = 4;

/** Counts are kept apart on a grid of square cells this wide (pixels). */
export const COUNT_CELL_PX = 26;

/** Names are kept apart on a grid of cells this wide and high (pixels): one name per cell. */
export const LABEL_CELL_PX = { w: 72, h: 16 } as const;

/** Below this radius on screen, a node's picture is too small to make out: it is not drawn. */
export const PICTURE_MIN_PX = 6;

/** How much energy the simulation keeps each step. */
export const COOLING = 0.982;

/**
 * The same for big graphs (their pushes from a worker, a few steps a second): they start close
 * to their final shape (`state/scene/seed.ts`), so fewer steps do.
 */
export const BIG_COOLING = 0.965;

/** Below this energy the layout is considered at rest. */
export const REST_ENERGY = 0.005;

/** A paint slower than this (milliseconds) makes view moves deferred. */
export const SLOW_PAINT_MS = 14;

/** How long the view must rest before a deferred paint (milliseconds). */
export const REST_MS = 110;

/** During a long gesture, a deferred picture is painted again at least this often (milliseconds). */
export const REFRESH_MS = 250;

/** How close (in pixels) the pointer must be to a handle or a link end to grab it. */
export const GRAB_RADIUS_PX = 9;

/** How far (in pixels) a press must move before it counts as a drag rather than a click. */
export const DRAG_PX = 4;

/** How much the 3D camera turns per pixel of drag, in radians. */
export const TURN_PER_PIXEL = 0.008;

/** Radius of the link handle, in pixels. */
export const HANDLE_RADIUS_PX = 6;

/** Below this width on screen (pixels), a box is a plain rectangle: nothing in it could be seen. */
export const BOX_DETAIL_PX = 16;

/** How close to an arrow a click must be, in pixels. */
export const EDGE_TOLERANCE_PX = 6;

/** How close to a pin a press must be, in pixels. */
export const PIN_TOLERANCE_PX = 8;

/** How far up and left of the pointer a new box's corner goes, so the box sits under it. */
export const BOX_CORNER = { x: 40, y: 16 };

/** How long after a middle click a paste is taken as the click's own (Linux's primary selection). */
export const MIDDLE_PASTE_MS = 500;

/** How faint a link out of focus is drawn (another node focused, or not matching the search). */
export const OUT_OF_FOCUS_ALPHA = 0.07;

/** How faint a dimmed node (not matching the search, or far from the focused one) and a class diagram's arrow out of focus are drawn. */
export const DIMMED_ALPHA = 0.14;

/** A dotted line's dash and gap (pixels): mentions in the nodal view, dotted link kinds. */
export const DOTTED_DASH = [1.5, 3.5] as const;

/** The size of the small words drawn on the canvas: link labels, group names, bundle counts (pixels). */
export const SMALL_TEXT_PX = 10.5;

/** The radius of the handles at a selected link's ends (pixels). */
export const LINK_END_PX = 6;

/** How far outside a node its selection or hover ring is drawn (pixels). */
export const RING_GAP_PX = 6;

/** How much a "Zoom in" button or menu line zooms (its "Zoom out" divides by it). */
export const ZOOM_STEP = 1.25;
